import { useState } from "react";
import { useForm } from "react-hook-form";
import { CustomEventType } from "../../utils/types";
import { useCreateRecord } from "../../../../api/useCreateRecord";
import { useUpdateRecords } from "../../../../api/useUpdateRecords";
import { useDeleteRecords } from "../../../../api/useDeleteRecords";
import { useStore } from "../../../../store/store";
import { createWeekCopyItem, createDayCopyItems, createWeekCopyItemTimeOff, createDayCopyItemsTimeOff } from "./utils";
import { ScheduleActionFormData, ActionMode } from "./types";
import { getItems } from "../../../../api/items";

type UseScheduleActionsProps = {
    selectedEvents: Array<CustomEventType>;
    setSelectedEvents: (events: Array<CustomEventType>) => void;
    setSelectMode: (value: boolean) => void;
    filteredEvents: Array<CustomEventType>;
};

type LoadingStates = {
    isCopying: boolean;
    isUpdatingTimes: boolean;
    isUpdatingNotes: boolean;
    isDeleting: boolean;
    isUpdatingServices: boolean;
    isUpdatingRoom: boolean;
    isUpdatingClinic: boolean;
    isConverting: boolean;
};

export const useScheduleActions = ({
    selectedEvents,
    setSelectedEvents,
    setSelectMode,
    filteredEvents,
}: UseScheduleActionsProps) => {
    const [actionMode, setActionMode] = useState<ActionMode>(null);
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [loadingStates, setLoadingStates] = useState<LoadingStates>({
        isCopying: false,
        isUpdatingTimes: false,
        isUpdatingNotes: false,
        isDeleting: false,
        isUpdatingServices: false,
        isUpdatingRoom: false,
        isUpdatingClinic: false,
        isConverting: false,
    });

    const createSchedule = useCreateRecord();
    const updateRecords = useUpdateRecords();
    const deleteRecords = useDeleteRecords();
    const { notifications } = useStore();

    const formMethods = useForm<ScheduleActionFormData>({
        defaultValues: {
            selectedDays: [],
            selectedResource: null,
            selectedWeek: [],
            numberOfCopies: 1,
            newStartTime: "",
            newEndTime: "",
            noteText: "",
            selectedServices: [],
            targetRoomId: null,
            targetClinicId: null,
            applyToMatchingSlots: false,
            matchingEndDate: null,
        },
    });

    const { watch, reset, setValue } = formMethods;
    const watchedValues = watch();
    const isWeekModeActive = watchedValues.selectedWeek.length > 0;

    const handleActionModeChange = (mode: ActionMode, isUpdate = false) => {
        setActionMode(mode);
        setIsUpdateMode(isUpdate);
        if (mode === 'copy' || mode === 'update') {
            setValue("numberOfCopies", isUpdate ? 0 : 1);
        }
    };
    
    // Helper functions
    const showNotification = (message: string, severity: "success" | "error") => {
        notifications.setNotification({ message, severity, isOpen: true });
    };

    const generateErrorMessage = (error: any) => {
        return error.response?.data?.errors?.map((err: any) => err.message).join(", ") || "Erro desconhecido";
    };

    const setLoadingState = (key: keyof LoadingStates, value: boolean) => {
        setLoadingStates(prev => ({ ...prev, [key]: value }));
    };

    const resetFormAndCloseDialog = () => {
        setActionMode(null);
        setIsUpdateMode(false);
        reset();
        setSelectMode(false);
        setSelectedEvents([]);
    };

    const findMatchingFutureEvents = async (
        events: Array<CustomEventType>,
        endDate: Date,
        eventType: CustomEventType["type"],
        entity: string
    ): Promise<Array<number>> => {
        const filteredEvents = events.filter((e) => e.type === eventType);
        if (filteredEvents.length === 0) {
            return [];
        }

        const allMatchingIds: Array<number> = [];

        for (const event of filteredEvents) {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            const eventWeekday = eventStart.getDay();
            const eventStartHours = eventStart.getHours();
            const eventStartMinutes = eventStart.getMinutes();
            const eventEndHours = eventEnd.getHours();
            const eventEndMinutes = eventEnd.getMinutes();

            const { items } = await getItems(entity, "id,start_date,end_date,user.id", {
                filter: {
                    user: { id: { _eq: event.userId } },
                    start_date: { _gte: eventStart.toISOString(), _lte: endDate.toISOString() },
                },
                pageSize: -1,
            });

            const matchingItems = items.filter((item: any) => {
                const itemStart = new Date(item.start_date);
                const itemEnd = new Date(item.end_date);

                const isSameWeekday = itemStart.getDay() === eventWeekday;
                const isSameStartTime = itemStart.getHours() === eventStartHours &&
                                        itemStart.getMinutes() === eventStartMinutes;
                const isSameEndTime = itemEnd.getHours() === eventEndHours &&
                                      itemEnd.getMinutes() === eventEndMinutes;

                return isSameWeekday && isSameStartTime && isSameEndTime;
            });

            const matchingIds = matchingItems.map((item: any) => item.id);
            allMatchingIds.push(...matchingIds);
        }

        const uniqueMatchingIds = [...new Set(allMatchingIds)];
        return uniqueMatchingIds;
    };

    const getEventIdsForAction = async (
        eventType: CustomEventType["type"],
        entity: string
    ): Promise<Array<number>> => {
        const { applyToMatchingSlots, matchingEndDate } = watchedValues;
        const selectedIds = selectedEvents
            .filter((e) => e.type === eventType)
            .map((e) => e.id);

        if (!applyToMatchingSlots || !matchingEndDate) {
            return selectedIds;
        }

        const matchingIds = await findMatchingFutureEvents(selectedEvents, matchingEndDate, eventType, entity);
        const allIds = [...new Set([...selectedIds, ...matchingIds])];
        return allIds;
    };

    // Actions
    const handleSelectAll = () => {
        setSelectedEvents(filteredEvents);
    };

    const copyEventsToTargetDatesOrWeek = async (userIds?: Array<string>) => {
        const numberOfCopiesToCreate = watchedValues.numberOfCopies || 1;

        const scheduleEvents = selectedEvents.filter((e) => e.type === "schedule");
        const timeOffEvents = selectedEvents.filter((e) => e.type === "time_off");

        const newScheduleItems = scheduleEvents.flatMap((originalEvent) =>
            Array.from({ length: numberOfCopiesToCreate }).flatMap(() =>
                (userIds || [originalEvent.userId]).flatMap((targetUserId) => {
                    if (isWeekModeActive && watchedValues.selectedWeek.length > 0) {
                        return watchedValues.selectedWeek.flatMap((weekValue) =>
                            createWeekCopyItem(originalEvent, targetUserId, weekValue)
                        );
                    }
                    return createDayCopyItems(
                        originalEvent,
                        targetUserId,
                        watchedValues.selectedDays,
                        watchedValues.selectedResource
                    );
                })
            )
        );

        const newTimeOffItems = timeOffEvents.flatMap((originalEvent) =>
            Array.from({ length: numberOfCopiesToCreate }).flatMap(() =>
                (userIds || [originalEvent.userId]).flatMap((targetUserId) => {
                    if (isWeekModeActive && watchedValues.selectedWeek.length > 0) {
                        return watchedValues.selectedWeek.flatMap((weekValue) =>
                            createWeekCopyItemTimeOff(originalEvent, targetUserId, weekValue)
                        );
                    }
                    return createDayCopyItemsTimeOff(
                        originalEvent,
                        targetUserId,
                        watchedValues.selectedDays
                    );
                })
            )
        );

        try {
            setLoadingState("isCopying", true);
            if (newScheduleItems.length > 0) {
                await createSchedule.mutateAsync({ entity: "schedules", data: newScheduleItems });
            }
            if (newTimeOffItems.length > 0) {
                await createSchedule.mutateAsync({ entity: "time_off", data: newTimeOffItems });
            }
            showNotification("Eventos copiados com sucesso", "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isCopying", false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoadingState("isDeleting", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");
            const timeOffIds = await getEventIdsForAction("time_off", "time_off");
            if (scheduleIds.length > 0) {
                await deleteRecords.mutateAsync({ entity: "schedules", ids: scheduleIds });
            }
            if (timeOffIds.length > 0) {
                await deleteRecords.mutateAsync({ entity: "time_off", ids: timeOffIds });
            }
            const totalDeleted = scheduleIds.length + timeOffIds.length;
            showNotification(`${totalDeleted} eventos apagados com sucesso`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isDeleting", false);
        }
    };

    const handleUpdateTechnician = async (userIds: Array<string>) => {
        if (userIds.length !== 1) return;

        const timeOffUpdates = selectedEvents
            .filter((e) => e.type === "time_off")
            .map((event) => ({ id: event.id, user: userIds[0] }));

        try {
            setLoadingState("isCopying", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");
            const scheduleUpdates = scheduleIds.map((id) => ({ id, user: userIds[0] }));

            if (scheduleUpdates.length > 0) {
                await updateRecords.mutateAsync({ entity: "schedules", data: scheduleUpdates });
            }
            if (timeOffUpdates.length > 0) {
                await updateRecords.mutateAsync({ entity: "time_off", data: timeOffUpdates });
            }
            const totalUpdated = scheduleUpdates.length + timeOffUpdates.length;
            showNotification(`Técnico atualizado em ${totalUpdated} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isCopying", false);
        }
    };

    const handleUpdateTimes = async () => {
        const { newStartTime, newEndTime } = watchedValues;

        if (!newStartTime && !newEndTime) {
            showNotification("Por favor, selecione pelo menos um horário para atualizar", "error");
            return;
        }

        if (newStartTime && newEndTime && newStartTime >= newEndTime) {
            showNotification("O horário de início deve ser anterior ao horário de fim", "error");
            return;
        }

        try {
            setLoadingState("isUpdatingTimes", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");

            const { items: schedules } = await getItems("schedules", "id,start_date,end_date", {
                filter: { id: { _in: scheduleIds } },
                pageSize: scheduleIds.length,
            });

            const updatesData = schedules.map((schedule: any) => {
                const eventDate = new Date(schedule.start_date);
                const updateData: any = { id: schedule.id };

                if (newStartTime) {
                    const [startHours, startMinutes] = newStartTime.split(":").map(Number);
                    const newStart = new Date(eventDate);
                    newStart.setHours(startHours, startMinutes, 0, 0);
                    updateData.start_date = newStart.toISOString();
                }

                if (newEndTime) {
                    const [endHours, endMinutes] = newEndTime.split(":").map(Number);
                    const newEnd = new Date(eventDate);
                    newEnd.setHours(endHours, endMinutes, 0, 0);
                    updateData.end_date = newEnd.toISOString();
                }

                return updateData;
            });

            await updateRecords.mutateAsync({ entity: "schedules", data: updatesData });
            showNotification(`Horários atualizados em ${updatesData.length} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isUpdatingTimes", false);
        }
    };

    const handleUpdateNotes = async () => {
        if (!watchedValues.noteText.trim()) {
            showNotification("Por favor, insira uma nota", "error");
            return;
        }

        try {
            setLoadingState("isUpdatingNotes", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");
            const updatesData = scheduleIds.map((id) => ({
                id,
                notes: watchedValues.noteText.trim(),
            }));

            await updateRecords.mutateAsync({ entity: "schedules", data: updatesData });
            showNotification(`Notas atualizadas em ${updatesData.length} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isUpdatingNotes", false);
        }
    };

    const handleUpdateServices = async () => {
        const targetServiceIds = Array.from(new Set(watchedValues.selectedServices || []));
        if (!Array.isArray(targetServiceIds)) return;

        try {
            setLoadingState("isUpdatingServices", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");

            const { items } = await getItems("schedules", "id,services.id,services.services_id.id", {
                filter: { id: { _in: scheduleIds } },
                pageSize: scheduleIds.length,
            });

            const updatesData = items.map((schedule: any) => {
                const existingRelations = schedule?.services || [];
                const deleteIds = existingRelations.map((rel: any) => rel.id);

                const createItems = targetServiceIds.map((serviceId: number) => ({
                    schedules_id: schedule.id,
                    services_id: { id: serviceId },
                }));

                const data = {
                    id: schedule.id,
                    services: {
                        create: createItems,
                        update: [],
                        delete: deleteIds,
                    },
                } as any;

                return data;
            });

            await updateRecords.mutateAsync({ entity: "schedules", data: updatesData });
            showNotification(`Serviços atualizados em ${updatesData.length} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isUpdatingServices", false);
        }
    };

    const handleRemoveServices = async () => {
        try {
            setLoadingState("isUpdatingServices", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");

            if (scheduleIds.length === 0) {
                showNotification("Nenhum evento selecionado para remover serviços", "error");
                return;
            }

            const { items } = await getItems("schedules", "id,services.id", {
                filter: { id: { _in: scheduleIds } },
                pageSize: scheduleIds.length,
            });

            if (!items || items.length === 0) {
                showNotification("Não foram encontrados eventos para atualizar", "error");
                return;
            }

            const updatesData = items
                .filter((schedule: any) => schedule?.services?.length > 0)
                .map((schedule: any) => {
                    const deleteIds = schedule.services.map((rel: any) => rel.id);

                    return {
                        id: schedule.id,
                        services: {
                            create: [],
                            update: [],
                            delete: deleteIds,
                        },
                    } as any;
                });

            if (updatesData.length === 0) {
                showNotification("Os eventos selecionados não têm serviços para remover", "error");
                return;
            }

            await updateRecords.mutateAsync({ entity: "schedules", data: updatesData });
            showNotification(`Serviços removidos de ${updatesData.length} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isUpdatingServices", false);
        }
    };

    const handleUpdateRoom = async () => {
        const targetRoomId = watchedValues.targetRoomId;
        if (!targetRoomId) {
            showNotification("Por favor, selecione uma sala", "error");
            return;
        }

        try {
            setLoadingState("isUpdatingRoom", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");
            const updatesData = scheduleIds.map((id) => ({
                id,
                room: targetRoomId,
            }));

            await updateRecords.mutateAsync({ entity: "schedules", data: updatesData });
            showNotification(`Sala atualizada em ${updatesData.length} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isUpdatingRoom", false);
        }
    };

    // Converts selected schedule events into time_off entries of the chosen type, keeping the exact same
    // start/end (and so the same hours). The schedule's data is read first, then the schedule is DELETED
    // before the time_off is created — the time_off would otherwise overlap the still-existing schedule
    // and be rejected by the overlap validation.
    const handleConvertToTimeOff = async (timeOffType: string) => {
        const scheduleIds = selectedEvents.filter((event) => event.type === "schedule").map((event) => event.id);
        if (scheduleIds.length === 0) {
            showNotification("Selecione eventos de horário para converter", "error");
            return;
        }

        try {
            setLoadingState("isConverting", true);
            const { items } = await getItems("schedules", "id,start_date,end_date,user.id,clinic_id,notes", {
                filter: { id: { _in: scheduleIds } },
                pageSize: scheduleIds.length,
            });

            const newTimeOffItems = items.map((schedule: any) => ({
                clinic_id: schedule.clinic_id,
                start_date: schedule.start_date,
                end_date: schedule.end_date,
                user: schedule.user?.id,
                type: timeOffType,
                notes: schedule.notes ?? null,
            }));

            await deleteRecords.mutateAsync({ entity: "schedules", ids: scheduleIds });
            await createSchedule.mutateAsync({ entity: "time_off", data: newTimeOffItems });

            showNotification(`${scheduleIds.length} evento(s) convertido(s)`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isConverting", false);
        }
    };

    const handleUpdateClinic = async () => {
        const targetClinicId = watchedValues.targetClinicId;
        if (!targetClinicId) {
            showNotification("Por favor, selecione uma clínica", "error");
            return;
        }

        const timeOffUpdates = selectedEvents.filter((e) => e.type === "time_off").map((event) => ({
            id: event.id,
            clinic_id: targetClinicId,
        }));

        try {
            setLoadingState("isUpdatingClinic", true);
            const scheduleIds = await getEventIdsForAction("schedule", "schedules");
            const scheduleUpdates = scheduleIds.map((id) => ({
                id,
                clinic_id: targetClinicId,
            }));

            if (scheduleUpdates.length > 0) {
                await updateRecords.mutateAsync({ entity: "schedules", data: scheduleUpdates });
            }
            if (timeOffUpdates.length > 0) {
                await updateRecords.mutateAsync({ entity: "time_off", data: timeOffUpdates });
            }
            const totalUpdated = scheduleUpdates.length + timeOffUpdates.length;
            showNotification(`Clínica atualizada em ${totalUpdated} eventos`, "success");
            resetFormAndCloseDialog();
        } catch (error: any) {
            showNotification(generateErrorMessage(error), "error");
        } finally {
            setLoadingState("isUpdatingClinic", false);
        }
    };

    return {
        // Form methods
        formMethods,
        watchedValues,
        isWeekModeActive,

        // UI state
        actionMode,
        isUpdateMode,
        ...loadingStates,

        // Actions
        handleActionModeChange,
        handleSelectAll,
        copyEventsToTargetDatesOrWeek,
        handleDelete,
        handleUpdateTechnician,
        handleUpdateTimes,
        handleUpdateNotes,
        handleUpdateServices,
        handleRemoveServices,
        handleUpdateRoom,
        handleUpdateClinic,
        handleConvertToTimeOff,
        resetFormAndCloseDialog,
    };
}; 